import { observable, computed, action } from 'mobx';

import { SerializedData } from 'bernie-core';
import { AnalyticsStore, Store } from 'bernie-plugin-mobx';
import { getBexApiContext } from 'bernie-context';
import { RequestOptions } from 'bernie-graphql';
import { NOOP_LOGGER, SystemEvent, SystemEventLevel } from 'bernie-logger';
import { Level, liveAnnounce } from 'uitk-react-utils';
import { DetailsAndFaresStore } from '.';
import { trackAnalyticsList } from 'utility';
import {
  AvailableFare,
  FlightsListing,
  JourneyType,
  ProgressBarDetails,
  StandardOfferType
} from 'types/search/listings';
import { AnalyticsType } from 'types/shared';
import {
  FlightsFaresSeparationType,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsShoppingPwaFlightSearchResultsLoadingFlightsSearch,
  FlightsListingsViewType
} from '__generated__/api';
import ListingsAction = FlightsShoppingPwaFlightSearchResultsFlightsSearch.ListingsAction;
import PaginationConfig = FlightsShoppingPwaFlightSearchResultsLoadingFlightsSearch.PaginationConfig;
import { ClientMetadataStore } from 'stores/client-metadata-store';
import { SmartShoppingListingError } from '@shared-ui/retail-flights-smart-shopping-listings';

export interface ListingsType {
  accessibilityMessage: string;
  appliedSortingAccessibility: string;
  appliedFilterAccessibility: string;
  flightsListingsAnalytics: FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsListingsAnalytics;
  listings: FlightsListing[];
  listingsAction: ListingsAction;
  moreListingsAvailable: string;
  pinnedListingUnavailableMessage: string;
  showMoreListingLoadedAccessibilityMessage: string;
  listingsSkeletonCount: number;
  message: string;
  originDestination: string;
  progressBarDetails: ProgressBarDetails;
  paginationConfig: PaginationConfig[];
}

export class ListingsStore extends Store {
  @observable public listingResult: ListingsType;
  @observable public multiStopListingResult: ListingsType;

  @observable public loading: boolean;
  @observable public pendingRequests = 0;
  @observable public shouldShowSkeletons: boolean;
  @observable public showMoreLoading: boolean;
  @observable public smartShoppingLoading: boolean;
  @observable public isServerLoadingState: boolean;
  @observable public smartShoppingNoResponse: boolean;
  @observable public smartShoppingError: boolean;
  @observable public hasNonStopConfig: boolean;
  @observable public hasNonStopConfigWithFilters: boolean;
  @observable public hasNonStopListingError: boolean;
  @observable public smartShoppingShowFlexAndFilters: boolean;

  @observable public hasNonStopSlicesResolved: boolean;
  @observable public hasMultiStopSlicesResolved: boolean;

  private maxViewOfferAnalytics?: AnalyticsType[] = null;

  private hasNonStopPaginationConfig = () => {
    return this.listingResult?.paginationConfig?.filter(
      ({ faresSeparationType }) =>
        faresSeparationType === FlightsFaresSeparationType.NONSTOP_ONLY_BASE ||
        faresSeparationType === FlightsFaresSeparationType.NONSTOP_BASE_AND_UPSELL ||
        faresSeparationType === FlightsFaresSeparationType.NONSTOP_ONLY_UPSELL
    ).length
      ? true
      : false;
  };

  @computed get offersFound(): boolean {
    return !this.loading && this.listingResult.listings.length > 0;
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  public setMaxViewOfferAnalytics(value: AnalyticsType[]) {
    this.maxViewOfferAnalytics = value;
  }

  public trackOnViewedAnalytics = (analytics: AnalyticsStore) => {
    this.maxViewOfferAnalytics && trackAnalyticsList(this.maxViewOfferAnalytics, analytics);
  };

  @action
  public setHasNonStopConfig = () => {
    this.hasNonStopConfig = this.hasNonStopPaginationConfig();
  };

  @action
  public setHasNonStopConfigWithFilters = (hasNonStopConfigWithFilters: boolean) => {
    this.hasNonStopConfigWithFilters = this.hasNonStopPaginationConfig() && hasNonStopConfigWithFilters;
  };

  @action
  public setHasNonStopListingError = (value: boolean) => {
    this.hasNonStopListingError = value;
  };

  @action
  public setHasNonStopSlicesResolved = (value: boolean) => {
    this.hasNonStopSlicesResolved = value;
  };

  @action
  public setHasMultiStopSlicesResolved = (value: boolean) => {
    this.hasMultiStopSlicesResolved = value;
  };

  public updateStoreListings(showMoreListingsStore: ListingsStore) {
    if (showMoreListingsStore) {
      if (showMoreListingsStore.listingResult.listings !== null) {
        this.listingResult.listings = this.listingResult.listings.concat(showMoreListingsStore.listingResult.listings);
      }
      this.listingResult.listingsAction = showMoreListingsStore.listingResult.listingsAction;
      this.listingResult.moreListingsAvailable = showMoreListingsStore.listingResult.moreListingsAvailable;
    }
  }

  public updateStoreNonStopListings(showMoreListingsStore: ListingsStore) {
    if (showMoreListingsStore) {
      if (showMoreListingsStore.listingResult.listings !== null) {
        this.updateStoreListings(showMoreListingsStore);
        this.updateMultiStopResultsToMainListings();
      }
    }
  }

  public updateMultiStopResultsToMainListings() {
    if (this.hasMultiStopSlicesResolved) {
      this.listingResult.listings = this.listingResult.listings.concat(this.multiStopListingResult.listings);
      this.listingResult.listingsAction = this.multiStopListingResult.listingsAction;
      this.listingResult.moreListingsAvailable = this.multiStopListingResult.moreListingsAvailable;
    }
  }

  public updateStoreMultiStopListings(showMoreListingsStore: ListingsStore) {
    if (showMoreListingsStore) {
      if (showMoreListingsStore.listingResult.listings !== null) {
        this.multiStopListingResult.listings = this.multiStopListingResult.listings.concat(
          showMoreListingsStore.listingResult.listings
        );
        this.multiStopListingResult.listingsAction = showMoreListingsStore.listingResult.listingsAction;
        this.multiStopListingResult.moreListingsAvailable = showMoreListingsStore.listingResult.moreListingsAvailable;
        if (this.hasNonStopSlicesResolved) {
          this.updateStoreListings(showMoreListingsStore);
        }
      }
    }
  }

  public updateStoreListingsWithUpsells(availableFares: AvailableFare[], detailsAndFaresStore: DetailsAndFaresStore) {
    if (availableFares) {
      this.listingResult.listings.map(offer => {
        switch (offer.__typename) {
          case 'FlightsStandardOffer':
          case 'FlightsBargainFareOffer':
            const { journeyContinuationId } = offer;
            const availableFare = availableFares.filter(
              availableFare => availableFare.journeyContinuationId === journeyContinuationId
            );
            if (availableFare && availableFare[0]) {
              const { flightsJourneyAvailableFaresInformation } = availableFare[0];
              (offer.journeys as JourneyType[]).map(journey => {
                journey.flightsJourneyAvailableFaresInformation = flightsJourneyAvailableFaresInformation;
              });
            }
            if (journeyContinuationId === detailsAndFaresStore.currentOfferSelectedjourneyContinuationId) {
              detailsAndFaresStore.setIsUpsellLoaded(true);
              detailsAndFaresStore.setAvailableFaresInformationFromUpsellSlicing(offer.journeys);
            }
        }
      });
    }
  }

  public async updateListings(contextStore, searchCriteriaStore, searchResults, analytics) {
    let listingType = FlightsListingsViewType.SINGLE;
    if (this.hasNonStopConfig && !this.hasNonStopConfigWithFilters) {
      listingType = FlightsListingsViewType.MIXED;
    }
    try {
      const context = getBexApiContext(contextStore);
      const searchVariables = { context, flightsListingsViewType: listingType, ...searchCriteriaStore.searchCriteria };
      searchVariables.rewardsOption = searchCriteriaStore.rewardsOption;
      const requestOptions: RequestOptions = {
        pageId: searchCriteriaStore.pageId,
        extraHeaders: { 'Device-User-Agent-ID': contextStore && contextStore.deviceId }
      };

      const { listingResult } = (await searchResults.fetchResults(searchVariables, requestOptions, analytics)) || {};
      if (listingResult) {
        const showMoreListingsStore = new ListingsStore({ listingResult }, NOOP_LOGGER);
        showMoreListingsStore.hydrate({ listingResult });
        this.updateStoreListings(showMoreListingsStore);
      }
    } catch (error) {
      NOOP_LOGGER.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'SHOW_MORE_LISTINGS'), error);
    }
    liveAnnounce(this.listingResult.showMoreListingLoadedAccessibilityMessage, Level.POLITE);
    this.showMoreLoading = false;
  }

  // TODO : Remove this method once it is verified that there is parity between only_base and upsell calls for fastsearch
  public loggingOfferMismatch(availableFares: AvailableFare[], clientMetadataStore: ClientMetadataStore): string {
    if (availableFares) {
      let parity = false;
      const onlyBaseListings = this.listingResult.listings
        .filter(listing => listing.__typename === 'FlightsStandardOffer')
        .slice(0, availableFares.length - 1);
      onlyBaseListings.some(offer => {
        const { journeyContinuationId } = offer as StandardOfferType;
        const index = availableFares.findIndex(
          availableFare => availableFare.journeyContinuationId === journeyContinuationId
        );
        if (index === -1) {
          parity = true;

          return true;
        }
      });
      if (parity) {
        const jcidUpsell = this.getJcidFromAvailableFares(availableFares);
        const jcidBase = this.getJcidFromListings(onlyBaseListings);
        const isFastSearch = this.isFastSearch(clientMetadataStore);

        const logValue = `Upsell and Base offer mismatch isFastSearchEnabled=${isFastSearch} ; jcidInBase=${jcidBase} ; jcidInUpsell=${jcidUpsell}`;
        this.logger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'UPSELL_BASE_OFFER_MISMATCH'), logValue);

        return logValue;
      }
    }

    return null;
  }

  private isFastSearch(clientMetadataStore: ClientMetadataStore): boolean {
    return (
      clientMetadataStore.isResponseSummaryCachedML ||
      clientMetadataStore.isResponseSummaryCachedConfig ||
      clientMetadataStore.isResponseSummaryLive ||
      clientMetadataStore.isResponseSummaryHybrid
    );
  }

  private getJcidFromAvailableFares(availableFares: AvailableFare[]) {
    const jcidList = [];
    availableFares.forEach(availableFare => jcidList.push(availableFare.journeyContinuationId));

    return jcidList;
  }

  private getJcidFromListings(listings: FlightsListing[]): FlightsListing[] {
    const jcidList = [];
    if (listings?.length) {
      listings.forEach(offer => jcidList.push((offer as StandardOfferType).journeyContinuationId));
    }

    return jcidList;
  }

  public setLoading(loading: boolean): void {
    this.loading = loading;

    if (loading) {
      this.pendingRequests += 1;
    } else {
      this.pendingRequests = 0;
    }
  }

  public setSmartListingsLoading(loadingState: boolean): void {
    this.smartShoppingLoading = loadingState;
  }

  public setSmartListingsLoaded() {
    this.isServerLoadingState = false;
    this.smartShoppingLoading = false;
    this.smartShoppingNoResponse = false;
    this.smartShoppingShowFlexAndFilters = true;
  }

  public setSmartListingsError(listingError: SmartShoppingListingError) {
    this.isServerLoadingState = false;
    this.smartShoppingLoading = false;
    this.smartShoppingShowFlexAndFilters = false;
    if (listingError === SmartShoppingListingError.NO_API_RESPONSE) {
      this.smartShoppingNoResponse = true;
      this.logger.logEvent(
        new SystemEvent(SystemEventLevel.ERROR, 'SMART_SHOPPING_NO_API_RESPONSE'),
        SmartShoppingListingError.NO_API_RESPONSE
      );
    } else if (listingError === SmartShoppingListingError.SERVER_ERROR) {
      this.smartShoppingError = true;
      this.logger.logEvent(
        new SystemEvent(SystemEventLevel.ERROR, 'SMART_SHOPPING_SERVER_ERROR'),
        SmartShoppingListingError.SERVER_ERROR
      );
    } else if (listingError === SmartShoppingListingError.OVERFILTERING_ERROR) {
      this.logger.logEvent(
        new SystemEvent(SystemEventLevel.ERROR, 'SMART_SHOPPING_OVERFILTERING_ERROR'),
        SmartShoppingListingError.OVERFILTERING_ERROR
      );
    }
  }
}

ListingsStore.DEFAULTS = {
  loading: true,
  shouldShowSkeletons: false,
  showMoreLoading: false,
  listingResult: {
    listings: [],
    pinnedListingUnavailableMessage: '',
    progressBarDetails: {},
    listingsAction: {}
  },
  multiStopListingResult: {
    listings: [],
    pinnedListingUnavailableMessage: '',
    progressBarDetails: {},
    listingsAction: {}
  },
  smartShoppingLoading: true,
  isServerLoadingState: true,
  smartShoppingError: false,
  hasNonStopConfig: false,
  hasNonStopConfigWithFilters: false,
  hasNonStopListingError: false,
  smartShoppingNoResponse: false,
  smartShoppingShowFlexAndFilters: false
};
